import { ChevronDownIcon } from 'lucide-react'
import { MOODBOARD_ANALYZER_META, type MoodboardDepartment } from '@backend/lib/db/schema/moodboard'
import { Button } from '@/components/ui/shadcn/button'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/shadcn/popover'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/shadcn/tooltip'
import { cn } from '@/lib/utils'

// Caret next to Analyze that opens a popover of circular analyzer tiles. Each tile toggles
// an analyzer on/off (all on by default); a Radix tooltip explains what it contributes. At
// least one analyzer must stay on. Adding a new analyzer needs no change here — it renders
// from MOODBOARD_ANALYZER_META.
type Props = {
  enabled: MoodboardDepartment[]
  onToggle: (id: MoodboardDepartment) => void
  disabled?: boolean
}

export function AnalyzeConfigPopover({ enabled, onToggle, disabled }: Props) {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          size="icon-sm"
          disabled={disabled}
          className="rounded-l-md rounded-r-full"
          aria-label="Choose analyzers">
          <ChevronDownIcon size={14} />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        sideOffset={8}
        // Don't autofocus a tile on open — Radix tooltips open on focus, which would pop a
        // tooltip the moment the menu appears. Focus-on-open off = tooltips are hover-only.
        onOpenAutoFocus={(e) => e.preventDefault()}
        className="w-56 space-y-3 p-3">
        <div className="space-y-0.5">
          <p className="text-foreground text-sm font-medium">Analyzers</p>
          <p className="text-muted-foreground text-xs leading-snug">
            Pick which agents run on Analyze.
          </p>
        </div>
        <TooltipProvider delayDuration={200}>
          <div className="flex items-center gap-2.5">
            {MOODBOARD_ANALYZER_META.map(({ id, label, description }) => {
              const on = enabled.includes(id)
              const isOnlyOne = on && enabled.length === 1
              return (
                <Tooltip key={id}>
                  <TooltipTrigger asChild>
                    <button
                      type="button"
                      onClick={() => {
                        if (!isOnlyOne) onToggle(id)
                      }}
                      aria-pressed={on}
                      aria-label={label}
                      className={cn(
                        'flex h-8 w-8 items-center justify-center rounded-full border text-xs font-semibold transition-colors',
                        on
                          ? 'border-primary bg-primary text-primary-foreground'
                          : 'border-border text-muted-foreground hover:bg-hover bg-transparent',
                        isOnlyOne && 'cursor-not-allowed',
                      )}>
                      {label.slice(0, 1)}
                    </button>
                  </TooltipTrigger>
                  <TooltipContent side="bottom" className="max-w-52">
                    <p className="font-medium">{on ? label : `${label} — off`}</p>
                    <p className="text-tooltip-foreground/70">{description}</p>
                    {isOnlyOne && (
                      <p className="text-tooltip-foreground/70 mt-1">At least one must stay on.</p>
                    )}
                  </TooltipContent>
                </Tooltip>
              )
            })}
          </div>
        </TooltipProvider>
      </PopoverContent>
    </Popover>
  )
}
